import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PillBadge } from "./Primitives";
import { AlertOctagon, Wrench, FileSignature, Route, Clock, ExternalLink } from "lucide-react";

// Type → icon + verbose label. Kept alongside the render so it's obvious how
// each event surfaces in the timeline.
const TYPE_META = {
  incident:    { Icon: AlertOctagon,  label: "Incident" },
  maintenance: { Icon: Wrench,        label: "Maintenance" },
  dar:         { Icon: FileSignature, label: "Daily Activity" },
  tour:        { Icon: Route,         label: "Tour Scan" },
};

/**
 * Compact horizontal timeline of a site's 5 most recent reports.
 *
 * Renders only when the backend returns items; hidden entirely on empty
 * state so an admin editing a brand-new site doesn't see an empty box.
 * Each dot links to its filtered page (single click drills into the
 * relevant report list). Timestamps are relative for scannability.
 */
export default function SiteRecentActivity({ siteId, testId = "site-recent-activity" }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    api.get(`/sites/${siteId}/recent-activity`, { params: { limit: 5 } })
      .then((r) => { if (alive) setItems(r.data || []); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [siteId]);

  if (err) return null;
  if (items === null) {
    return (
      <div data-testid={`${testId}-loading`} className="text-[10px] uppercase tracking-widest text-zinc-500">
        Loading recent activity…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div data-testid={`${testId}-empty`} className="text-[10px] uppercase tracking-widest text-zinc-500 italic">
        No reports filed for this site yet.
      </div>
    );
  }

  return (
    <div data-testid={testId} className="space-y-1.5">
      <div className="label-mute flex items-center gap-1.5">
        <Clock className="size-3" /> Recent activity (5 latest)
      </div>
      <ol className="space-y-1">
        {items.map((it) => {
          const meta = TYPE_META[it.type] || TYPE_META.incident;
          const Icon = meta.Icon;
          return (
            <li key={`${it.type}-${it.id}`} data-testid={`${testId}-item-${it.id}`}>
              <Link
                to={it.link || "/"}
                className="flex items-start gap-2 py-1.5 px-2 hover:bg-[#1c1c1e] transition-colors group text-xs -mx-2"
                title={`${meta.label} · ${new Date(it.created_at).toLocaleString()}`}
              >
                <PillBadge tone={it.tone || "default"} className="!py-0 !text-[9px] mt-0.5">
                  <Icon className="size-3 mr-0.5" />
                  {meta.label}
                </PillBadge>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-200 truncate">{it.title}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">
                    {new Date(it.created_at).toLocaleString()}
                    {it.severity && <span className="ml-2 uppercase">· {it.severity}</span>}
                    {it.status && <span className="ml-2 uppercase">· {it.status.replace("_", " ")}</span>}
                  </div>
                </div>
                <ExternalLink className="size-3 text-zinc-600 group-hover:text-[#FF3B30] mt-1 transition-colors flex-shrink-0" />
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
